import { ChangeEvent, FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiDetailFields, MiSectionHeader } from "@miplatform/components";
import { ACCOUNT_TYPE, INPUT_TYPES } from "@miplatform/constants";
import {
  AccountType,
  IAccountResponse,
  IStepTemplateData,
} from "@miplatform/types";
import { getHoursAndMinutes, toMinutes } from "@miplatform/utils";
import { useAccounts } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";

// time validation helpers
const isValidHours = (hours: number) => hours >= 0 && hours <= 23;
const isValidMinutes = (minutes: number) => minutes >= 0 && minutes <= 59;

export const WorkOrderStepDurationTabView: FC<
  WorkOrderStepDurationTabViewProps
> = ({ form, extraTabProps = {} }) => {
  const { isReadOnly } = extraTabProps;
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = form;

  const { duration = 0, builderDurations = [] } = watch();
  const { classes } = useStyles();
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [ACCOUNT_TYPE.BUILDER] as AccountType[],
  });

  useEffect(() => {
    getAccounts();
  }, []);

  const defaultDurationField = () => {
    const { hours, minutes } = getHoursAndMinutes(duration);

    const fields = [
      {
        label: "Hours",
        inputProps: { type: "number" },
        formControl: control,
        name: "duration",
        disabled: isReadOnly,
        value: hours,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newHours = Number(e.target.value);
          if (!isValidHours(newHours)) {
            return;
          }
          setValue("duration", toMinutes(newHours, minutes), {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        label: "Minutes",
        inputProps: { type: "number" },
        formControl: control,
        name: "duration",
        disabled: isReadOnly,
        value: minutes,
        required: true,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newMinutes = Number(e.target.value);
          if (!isValidMinutes(newMinutes)) {
            return;
          }
          setValue("duration", toMinutes(hours, newMinutes), {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      } as any,
    ];

    return fields;
  };

  const builderDurationField = (account: IAccountResponse) => {
    const builder = builderDurations.find((b) => b.builderId === account.id);
    const { hours, minutes } = getHoursAndMinutes(builder?.duration);
    const hasError =
      builder?.builderId && builder?.duration === 0 && errors.builderDurations;

    const fields = [
      {
        label: "Custom Duration",
        fieldType: INPUT_TYPES.CHECKBOX,
        disabled: isReadOnly,
        value: builder?.builderId,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked) {
            const newBuilderDuration = { builderId: account.id, duration: 0 };
            const updated = [...builderDurations, newBuilderDuration];
            setValue("builderDurations", updated, {
              shouldDirty: true,
              shouldValidate: true,
            });
          } else {
            const updated = builderDurations.filter(
              (b) => b.builderId !== account.id
            );
            setValue("builderDurations", updated, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        },
      },
      {
        label: "Hours",
        inputProps: { type: "number" },
        disabled: isReadOnly || !builder?.builderId,
        formControl: control,
        error: hasError,
        helperText: hasError && errors.builderDurations?.message,
        value: hours,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newHours = Number(e.target.value);
          if (!isValidHours(newHours)) {
            return;
          }
          const updated = builderDurations.map((b) => {
            if (b.builderId === account.id) {
              return { ...b, duration: toMinutes(newHours, minutes) };
            }
            return b;
          });
          setValue("builderDurations", updated, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
      {
        label: "Minutes",
        inputProps: { type: "number" },
        disabled: isReadOnly || !builder?.builderId,
        required: builder?.builderId,
        formControl: control,
        error: hasError,
        helperText: hasError && errors.builderDurations?.message,
        value: minutes,
        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
          const newMinutes = Number(e.target.value);
          if (!isValidMinutes(newMinutes)) {
            return;
          }
          const updated = builderDurations.map((b) => {
            if (b.builderId === account.id) {
              return { ...b, duration: toMinutes(hours, newMinutes) };
            }
            return b;
          });
          setValue("builderDurations", updated, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      } as any,
    ];

    return fields;
  };

  return (
    <ConfigAccordionHeader title="Step Duration">
      <Box className={classes.root}>
        <MiDetailFields
          headerProps={{
            title: "Default Step Template Duration",
            variant: "text",
          }}
          detailFields={defaultDurationField()}
        />
        <MiSectionHeader title="Set Duration Per Builder" variant="text" />
        <Box className={classes.builderList}>
          {accounts.map((a) => {
            return (
              <Box key={a.id} className={classes.builderCard}>
                <Box className={classes.builderNameBox}>
                  <Typography className={classes.builderName}>
                    {a.name}
                  </Typography>
                </Box>
                <Box className={classes.builderFieldsBox}>
                  <MiDetailFields detailFields={builderDurationField(a)} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </ConfigAccordionHeader>
  );
};

interface WorkOrderStepDurationTabViewProps {
  form: UseFormReturn<IStepTemplateData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  builderList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    margin: theme.spacing(1),
  },
  builderCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.common.white,
  },
  builderNameBox: {
    width: "30%",
  },
  builderFieldsBox: {
    width: "70%",
  },
  builderName: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
}));
